/**
 * ============================================================
 * ATHLETAIN — Data Models & Default Factories
 * ============================================================
 *
 * Centralized definitions for every data shape used in the app.
 * Import the defaults/factories wherever you need a guaranteed
 * shape (components, tests, seed scripts, etc.).
 *
 * Usage:
 *   import { createAthleteDefaults, ATHLETE_DEFAULTS } from '../models';
 *   const data = { ...ATHLETE_DEFAULTS, ...incomingData };
 * ============================================================
 */

// ────────────────────────────────────────────
// 1. Athlete (Supabase: `athletes` table)
// ────────────────────────────────────────────

/**
 * @typedef {Object} AthleteAttributes
 * @property {number}  age            - Athlete's age
 * @property {number}  weight         - Weight in kg
 * @property {number}  height         - Height in cm
 * @property {number}  points         - Ranking/profile points
 * @property {string}  sport          - Primary sport name
 * @property {string}  avatar_url     - Profile image URL
 * @property {string}  country        - Country of residence
 * @property {string}  birth_date     - ISO date string (YYYY-MM-DD)
 * @property {string}  [guardian_email] - Legal guardian email (minors only)
 */

/**
 * @typedef {Object} Athlete
 * @property {number|string} id          - Auto-increment or UUID
 * @property {string}  auth_id     - Supabase Auth user UUID (links to auth.users.id)
 * @property {string}  full_name   - Athlete's full name
 * @property {string}  email       - Unique email
 * @property {string}  position    - Playing position
 * @property {string}  category    - Level (e.g. "Profesional", "Semi-Profesional", "Amateur")
 * @property {AthleteAttributes} attributes - JSONB attributes column
 * @property {string}  created_at  - ISO timestamp (auto-set by Supabase)
 * @property {AthleteTracking[]} athlete_tracking - Related tracking records (joined)
 */

export const ATHLETE_ATTRIBUTES_DEFAULTS = {
  age: 0,
  weight: 0,
  height: 0,
  points: 0,
  sport: '',
  avatar_url: '',
  country: '',
  birth_date: '',
  guardian_email: '',
  // Datos estructurados para el matching scout (Scout QA #24). Football-first.
  // Los `*_code` salen de src/constants/footballTaxonomy.js; los labels
  // (main_position/secondary_position/laterality) se conservan para la UI y
  // para los atletas legados sin codes, que hacen match por texto.
  // `tags` = array de codes de la taxonomía.
  main_position: '',
  secondary_position: '',
  position_code: null,
  position_group: null,
  secondary_position_code: null,
  laterality: '',
  laterality_code: null,
  tags: [],
};

export const ATHLETE_DEFAULTS = {
  id: '',
  auth_id: '',
  full_name: '',
  email: '',
  position: '',
  category: '',
  attributes: { ...ATHLETE_ATTRIBUTES_DEFAULTS },
  created_at: '',
  athlete_tracking: [],
};

export const createAthleteDefaults = (overrides = {}) => ({
  ...ATHLETE_DEFAULTS,
  attributes: {
    ...ATHLETE_ATTRIBUTES_DEFAULTS,
    ...(overrides.attributes || {}),
  },
  ...overrides,
});

/**
 * Build an athlete row ready to INSERT into the `athletes` table
 * from sign-up form data. Matches the Supabase table schema.
 *
 * @param {Object} params
 * @param {string} params.authId       - Supabase Auth user UUID
 * @param {string} params.fullName
 * @param {string} params.email
 * @param {string} params.sport
 * @param {string} params.birthDate   - YYYY-MM-DD
 * @param {string} params.country
 * @param {string} [params.guardianEmail]
 * @returns {Object} row ready for supabase.from('athletes').insert()
 */
export const buildAthleteInsert = ({
  authId,
  fullName,
  email,
  sport,
  birthDate,
  country,
  guardianEmail = '',
}) => {
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;

  return {
    auth_id: authId,
    full_name: fullName,
    email,
    position: '',
    category: 'Amateur',
    attributes: {
      age,
      sport,
      height: 0,
      weight: 0,
      points: 0,
      avatar_url: '',
      country,
      birth_date: birthDate,
      ...(guardianEmail ? { guardian_email: guardianEmail } : {}),
    },
  };
};

// ────────────────────────────────────────────
// 1b. Athlete Career (Supabase: `athlete_career` table)
// ────────────────────────────────────────────

/**
 * @typedef {Object} AchievementEntry
 * @property {string} achievement - Achievement name
 * @property {string} club        - Club/team name
 * @property {string} date        - "mes año" e.g. "feb. 2023"
 * @property {string} location    - City, Country
 */

/**
 * @typedef {Object} AthleteCareer
 * @property {number|string} id               - Auto-increment PK
 * @property {number|string} athlete_id       - FK → athletes.id
 * @property {string}  current_club           - Current club/team
 * @property {string}  competition_level      - e.g. "Profesional", "Amateur"
 * @property {AchievementEntry[]} achievements - JSONB array of achievements
 * @property {string}  created_at             - ISO timestamp
 */

export const ATHLETE_CAREER_DEFAULTS = {
  id: '',
  athlete_id: '',
  current_club: '',
  competition_level: '',
  achievements: [],
  created_at: '',
};

// ────────────────────────────────────────────
// 1c. Athlete Preferences (Supabase: `athlete_preferences` table)
// ────────────────────────────────────────────

/**
 * @typedef {Object} AthletePreferences
 * @property {number|string} id                  - Auto-increment PK
 * @property {number|string} athlete_id          - FK → athletes.id
 * @property {string}  agency_status             - e.g. "Sin representación"
 * @property {string}  travel_availability       - "si" | "no" | "solo_becas"
 * @property {string}  main_objective            - Primary career goal
 * @property {string}  differentiator            - Max 140 chars description
 * @property {string}  created_at                - ISO timestamp
 */

export const ATHLETE_PREFERENCES_DEFAULTS = {
  id: '',
  athlete_id: '',
  agency_status: '',
  travel_availability: '',
  main_objective: '',
  differentiator: '',
  created_at: '',
};

// ────────────────────────────────────────────
// 2. Athlete Tracking Metrics
//    (Supabase: `athlete_tracking.metrics` JSONB)
// ────────────────────────────────────────────

// --- 2a. Calories ---

/**
 * @typedef {Object} CaloriesChartPoint
 * @property {string} time  - Label (e.g. "Lun", "Mar")
 * @property {number} value - Calorie value
 */

/**
 * @typedef {Object} CaloriesData
 * @property {number} consumed             - Total calories consumed
 * @property {CaloriesChartPoint[]} history - Chart history
 */

export const CALORIES_DEFAULTS = {
  consumed: 0,
  history: [],
};

// --- 2b. Heart Rate ---

/**
 * @typedef {Object} HeartRatePoint
 * @property {string} time  - Time label (e.g. "10:00")
 * @property {number} value - BPM value
 */

/**
 * @typedef {Object} HeartRateData
 * @property {number} current          - Current BPM
 * @property {number} average          - Average BPM
 * @property {HeartRatePoint[]} history - Chart history
 */

export const HEART_RATE_DEFAULTS = {
  current: 0,
  average: 0,
  history: [],
};

// --- 2c. Steps ---

/**
 * @typedef {Object} StepsPoint
 * @property {string} day   - Day label (e.g. "Lun")
 * @property {number} steps - Step count
 */

/**
 * @typedef {Object} StepsData
 * @property {number}       current   - Today's step count
 * @property {number}       yesterday - Yesterday's step count
 * @property {StepsPoint[]} history   - Chart history
 */

export const STEPS_DEFAULTS = {
  current: 0,
  yesterday: 0,
  history: [],
};

// --- 2d. Progress ---

/**
 * @typedef {Object} TrainingCategory
 * @property {number} progress - Completion percentage (0-100)
 * @property {number} sets     - Completed sets
 * @property {number} total    - Total sets
 * @property {string} type     - Exercise type label (e.g. "Running")
 */

/**
 * @typedef {Object} ProgressData
 * @property {number}           goalCompletion - Overall goal % (0-100)
 * @property {TrainingCategory} cardio         - Cardio training
 * @property {TrainingCategory} strength       - Strength training
 * @property {TrainingCategory} flexibility    - Flexibility training
 */

export const TRAINING_CATEGORY_DEFAULTS = {
  progress: 0,
  sets: 0,
  total: 0,
  type: 'N/A',
};

export const PROGRESS_DEFAULTS = {
  goalCompletion: 0,
  cardio: { ...TRAINING_CATEGORY_DEFAULTS },
  strength: { ...TRAINING_CATEGORY_DEFAULTS },
  flexibility: { ...TRAINING_CATEGORY_DEFAULTS },
};

// --- 2e. Meals ---

/**
 * @typedef {Object} Meal
 * @property {string} meal     - Meal type ("Desayuno", "Almuerzo", etc.)
 * @property {string} name     - Dish name
 * @property {number} calories - Calorie count
 * @property {string} size     - Portion size ("Grande", "Mediano", "Pequeño")
 * @property {string} image    - Image URL
 */

export const MEAL_DEFAULTS = {
  meal: '',
  name: '',
  calories: 0,
  size: '',
  image: '',
};

// --- 2f. Schedule ---

/**
 * @typedef {Object} ScheduleItem
 * @property {string}  time      - Time string (e.g. "08:00 AM")
 * @property {string}  activity  - Activity description
 * @property {string}  type      - Category ("Entrenamiento", "Salud", etc.)
 * @property {boolean} completed - Whether the activity is done
 */

export const SCHEDULE_ITEM_DEFAULTS = {
  time: '',
  activity: '',
  type: '',
  completed: false,
};

// --- 2g. Full Metrics (combines all above) ---

/**
 * @typedef {Object} AthleteMetrics
 * @property {CaloriesData}   calories  - Calories data
 * @property {HeartRateData}  heartRate - Heart rate data
 * @property {StepsData}      steps     - Steps data
 * @property {ProgressData}   progress  - Training progress
 * @property {Object}         training  - Training metrics grouped by sport
 * @property {Meal[]}         meals     - Meal plan
 * @property {ScheduleItem[]} schedule  - Daily schedule
 */

export const ATHLETE_METRICS_DEFAULTS = {
  calories: { ...CALORIES_DEFAULTS },
  heartRate: { ...HEART_RATE_DEFAULTS },
  steps: { ...STEPS_DEFAULTS },
  progress: { ...PROGRESS_DEFAULTS },
  training: {
    lastSport: '',
    lastUpdatedAt: '',
    bySport: {},
  },
  meals: [],
  schedule: [],
};

/**
 * @typedef {Object} AthleteTracking
 * @property {string}         id          - UUID
 * @property {string}         athlete_id  - FK to athletes.id
 * @property {string}         record_date - ISO timestamp
 * @property {AthleteMetrics} metrics     - All tracking data
 * @property {string}         notes       - Optional notes
 */

export const ATHLETE_TRACKING_DEFAULTS = {
  id: '',
  athlete_id: '',
  record_date: '',
  metrics: { ...ATHLETE_METRICS_DEFAULTS },
  notes: '',
};

// ────────────────────────────────────────────
// 3. Scout / Recruiter (Supabase: `scouts`)
// ────────────────────────────────────────────

/**
 * @typedef {Object} ScoutOrganization
 * @property {string} name          - Organization name
 * @property {string} address       - Street address
 * @property {string} country_phone - Organization phone
 * @property {string} tax_id        - Tax ID
 */

/**
 * @typedef {Object} ScoutNotifications
 * @property {boolean} email_alerts        - Email alert toggle
 * @property {boolean} push_notifications  - Push notification toggle
 * @property {boolean} sms_alerts          - SMS toggle
 * @property {boolean} marketing           - Marketing emails toggle
 */

/**
 * @typedef {Object} ScoutSubscription
 * @property {string}   plan_name    - Subscription plan
 * @property {string}   next_payment - ISO date of next payment
 * @property {string}   status       - "active" | "inactive" | "cancelled"
 * @property {string[]} benefits     - List of plan benefits
 */

/**
 * @typedef {Object} Scout
 * @property {string}            id                - UUID
 * @property {string}            full_name         - Scout's full name
 * @property {string}            email             - Unique email
 * @property {string}            phone             - Phone number
 * @property {string}            organization_name - Organization name
 * @property {Object}            details           - JSONB details column
 * @property {ScoutOrganization} details.organization_info
 * @property {ScoutNotifications} details.notifications
 * @property {Object}            details.public_profile
 * @property {Object}            details.preferences
 * @property {ScoutSubscription} details.subscription
 */

export const SCOUT_ORGANIZATION_DEFAULTS = {
  name: '',
  address: '',
  country_phone: '',
  tax_id: '',
};

export const SCOUT_NOTIFICATIONS_DEFAULTS = {
  email_alerts: false,
  push_notifications: false,
  sms_alerts: false,
  marketing: false,
};

export const SCOUT_SUBSCRIPTION_DEFAULTS = {
  plan_name: '',
  next_payment: '',
  status: 'inactive',
  benefits: [],
};

export const SCOUT_DEFAULTS = {
  id: '',
  full_name: '',
  email: '',
  phone: '',
  organization_name: '',
  details: {
    organization_info: { ...SCOUT_ORGANIZATION_DEFAULTS },
    notifications: { ...SCOUT_NOTIFICATIONS_DEFAULTS },
    public_profile: { url: '', is_visible: false },
    preferences: { language: 'Español', timezone: '' },
    subscription: { ...SCOUT_SUBSCRIPTION_DEFAULTS },
  },
};

export const createScoutDefaults = (overrides = {}) => ({
  ...SCOUT_DEFAULTS,
  details: {
    ...SCOUT_DEFAULTS.details,
    ...(overrides.details || {}),
  },
  ...overrides,
});

// ────────────────────────────────────────────
// 3b. Scout Recruiting Criteria
//     Free-text brief lives in `scouts.recruiting_brief`; this is the
//     structured JSON the LLM extracts from it and stores in
//     `scouts.criteria`. Consumed by the `match_athletes_for_scout` RPC.
// ────────────────────────────────────────────

/**
 * @typedef {Object} ScoutCriteria
 * @property {string|null}   sport                  - Required sport for hard filter (e.g. "Fútbol")
 * @property {string[]|null} positions              - Target positions (e.g. ["Delantero","Lateral derecho"])
 * @property {number|null}   age_min                - Inclusive lower age bound
 * @property {number|null}   age_max                - Inclusive upper age bound
 * @property {string|null}   competition_level_min  - Minimum acceptable competition level label
 * @property {string[]|null} regions                - Target countries / regions
 * @property {boolean|null}  scholarship_offered    - True for college/academy scouts offering scholarships
 * @property {string[]|null} key_traits             - Desired qualitative attributes (e.g. ["velocidad","definición"])
 */

export const SCOUT_CRITERIA_DEFAULTS = {
  sport: null,
  positions: null,
  age_min: null,
  age_max: null,
  competition_level_min: null,
  regions: null,
  scholarship_offered: null,
  key_traits: null,
  // Football taxonomy codes (Scout QA #24), derived server-side from the labels
  // above by /v1/scout-criteria. match_athletes_by_criteria compares these
  // exactly and falls back to the labels when they are null, so losing them
  // costs precision rather than results.
  position_codes: null,
  position_groups: null,
  tag_codes: null,
};

export const createScoutCriteriaDefaults = (overrides = {}) => ({
  ...SCOUT_CRITERIA_DEFAULTS,
  ...overrides,
});

/**
 * @typedef {Object} ScoutSearch
 * @property {number}              id
 * @property {number}              scout_id
 * @property {string}              title
 * @property {string|null}         recruiting_brief
 * @property {ScoutCriteria|null}  criteria
 * @property {string|null}         criteria_extracted_at
 * @property {string}              created_at
 * @property {string}              updated_at
 * @property {string|null}         archived_at
 * @property {boolean}             alerts_enabled - Aviso semanal de talento nuevo (por defecto activo)
 */

export const SCOUT_SEARCH_DEFAULTS = {
  id: null,
  scout_id: null,
  title: "Búsqueda sin título",
  recruiting_brief: "",
  criteria: null,
  criteria_extracted_at: null,
  created_at: "",
  updated_at: "",
  archived_at: null,
  alerts_enabled: true,
};

// ────────────────────────────────────────────
// 4. Scout Tracking (Supabase: `scout_tracking`)
// ────────────────────────────────────────────

/**
 * @typedef {Object} TalentFunnel
 * @property {number} new_talents - New talents discovered
 * @property {number} evaluating  - Currently evaluating
 * @property {number} contacted   - Contacted athletes
 */

/**
 * @typedef {Object} SportScore
 * @property {string} sport     - Sport name
 * @property {number} score     - Current score
 * @property {number} max_score - Maximum possible score
 */

/**
 * @typedef {Object} AverageRatings
 * @property {number} current_rating      - Current athlete rating avg
 * @property {number} potential_rating     - Potential rating avg
 * @property {number} average_improvement  - Improvement delta
 * @property {number} [scale]             - Rating scale (e.g. 100)
 */

/**
 * @typedef {Object} ScoutTrackingMetrics
 * @property {TalentFunnel}   talent_funnel   - Recruitment funnel
 * @property {SportScore[]}   top_sports      - Top scouted sports
 * @property {AverageRatings} average_ratings - Rating averages
 */

export const TALENT_FUNNEL_DEFAULTS = {
  new_talents: 0,
  evaluating: 0,
  contacted: 0,
};

export const AVERAGE_RATINGS_DEFAULTS = {
  current_rating: 0,
  potential_rating: 0,
  average_improvement: 0,
  scale: 100,
};

export const SCOUT_TRACKING_DEFAULTS = {
  id: '',
  scout_id: '',
  record_date: '',
  metrics: {
    talent_funnel: { ...TALENT_FUNNEL_DEFAULTS },
    top_sports: [],
    average_ratings: { ...AVERAGE_RATINGS_DEFAULTS },
  },
};

// ────────────────────────────────────────────
// 4b. Admin User (backend: `users` + joined profile)
//     Unified row returned by GET /admin/users. `role` is the first-class
//     backend enum ("athlete" | "scout" | "admin"); `is_active` drives the
//     soft-delete state. Account fields stay snake_case like every other API row.
// ────────────────────────────────────────────

/**
 * @typedef {Object} AdminUser
 * @property {string}  id          - User UUID (users.id)
 * @property {string}  full_name   - Display name (falls back to `name`)
 * @property {string}  [name]      - Alternate name field some endpoints return
 * @property {string}  email       - Unique email
 * @property {"athlete"|"scout"|"admin"} role - First-class backend role
 * @property {boolean} is_active   - false = soft-deleted / deactivated
 * @property {string}  [sport]     - Primary sport (athletes only)
 * @property {string}  created_at  - ISO timestamp
 */

export const ADMIN_USER_DEFAULTS = {
  id: '',
  full_name: '',
  name: '',
  email: '',
  role: 'athlete',
  is_active: true,
  sport: '',
  created_at: '',
};

export const createAdminUserDefaults = (overrides = {}) => ({
  ...ADMIN_USER_DEFAULTS,
  ...overrides,
});

/** Human display name for a user row regardless of which field the API filled. */
export const adminUserName = (row = {}) =>
  row.full_name || row.profile_name || row.name || row.email || 'Sin nombre';

// ────────────────────────────────────────────
// 5. Social Platform
// ────────────────────────────────────────────

/**
 * @typedef {Object} PostUser
 * @property {string} name     - User display name
 * @property {string} avatar   - Avatar image URL
 * @property {string} category - User category/team label
 */

/**
 * @typedef {Object} PostStats
 * @property {number} likes     - Like count
 * @property {number} comments  - Comment count
 * @property {number} shares    - Share count
 * @property {number} bookmarks - Bookmark count
 */

/**
 * @typedef {Object} SocialPost
 * @property {number}    id      - Post ID
 * @property {PostUser}  user    - Author info
 * @property {string}    time    - Relative time label
 * @property {string}    content - Post text
 * @property {string}    image   - Post image URL
 * @property {PostStats} stats   - Engagement stats
 */

export const SOCIAL_POST_DEFAULTS = {
  id: 0,
  user: { name: '', avatar: '', category: '' },
  time: '',
  content: '',
  image: '',
  stats: { likes: 0, comments: 0, shares: 0, bookmarks: 0 },
};

// ────────────────────────────────────────────
// 6. Notifications
// ────────────────────────────────────────────

/**
 * @typedef {Object} Notification
 * @property {number}      id          - Notification ID
 * @property {string}      type        - "system" | "talent" | "social" | "alert" | "saved_search_alert"
 * @property {React.Component} icon    - Lucide icon component
 * @property {string}      iconBg      - Tailwind bg class for icon
 * @property {string}      borderColor - Tailwind border class
 * @property {string}      title       - Notification title
 * @property {string}      titleColor  - Tailwind text color class
 * @property {string}      description - Body text
 * @property {string}      time        - Relative time string
 */

export const NOTIFICATION_DEFAULTS = {
  id: 0,
  type: 'system',
  icon: null,
  iconBg: 'bg-blue-400',
  borderColor: 'border-blue-300',
  title: '',
  titleColor: 'text-gray-900',
  description: '',
  time: '',
};

// ────────────────────────────────────────────
// 7. Athlete Form (personal data entry)
// ────────────────────────────────────────────

/**
 * @typedef {Object} AthleteFormPersonalData
 * @property {string} nombreCompleto - Full name
 * @property {string} fechaNacimiento - Birth date
 * @property {string|number} edad    - Age
 * @property {string} genero         - Gender
 * @property {string|number} peso    - Weight (kg)
 * @property {string|number} altura  - Height (cm)
 * @property {string} imc            - BMI
 * @property {string} deporte        - Sport
 * @property {string} posicion       - Position
 * @property {string} nivel          - Level
 * @property {string} experiencia    - Years of experience
 * @property {string} email          - Email
 * @property {string} telefono       - Phone
 * @property {string} nacionalidad   - Nationality
 * @property {string} ciudad         - City
 */

export const ATHLETE_FORM_DEFAULTS = {
  nombreCompleto: '',
  fechaNacimiento: '',
  edad: '',
  genero: '',
  peso: '',
  altura: '',
  imc: '',
  deporte: '',
  posicion: '',
  nivel: '',
  experiencia: '',
  email: '',
  telefono: '',
  nacionalidad: '',
  ciudad: '',
};

export const createAthleteFormDefaults = (initialData = {}) => {
  initialData = initialData || {};
  let attributes = initialData.attributes || {};
  if (typeof attributes === 'string') {
    try {
      attributes = JSON.parse(attributes || '{}');
    } catch {
      attributes = {};
    }
  }
  const career = Array.isArray(initialData.athlete_career)
    ? initialData.athlete_career[0] || {}
    : initialData.athlete_career || {};

  return {
    ...ATHLETE_FORM_DEFAULTS,
    nombreCompleto: initialData.full_name || initialData.name || '',
    fechaNacimiento:
      attributes.birth_date ||
      initialData.birthDate ||
      initialData.birth_date ||
      initialData.fechaNacimiento ||
      '',
    edad: attributes.age || initialData.age || '',
    genero: attributes.gender || attributes.genero || initialData.gender || '',
    peso: attributes.weight || initialData.weight || '',
    altura: attributes.height || initialData.height || '',
    imc: attributes.imc || initialData.bmi || '',
    deporte: attributes.sport || initialData.sport || '',
    posicion:
      initialData.position || attributes.main_position || initialData.position_name || '',
    nivel:
      career.competition_level ||
      initialData.category ||
      initialData.level ||
      '',
    experiencia:
      attributes.experience_years ||
      attributes.experience ||
      initialData.experience ||
      '',
    email: initialData.email || '',
    telefono: attributes.phone || initialData.phone || '',
    nacionalidad:
      attributes.country || initialData.nationality || initialData.country || '',
    ciudad: attributes.city || initialData.city || '',
  };
};
